import type { GenericRequest, PaginationMeta, PaginationQuery } from '@eliadmin/core/src/api/api-types';
import { buildAuthHeader, fetchData } from '@eliadmin/core/src/api/fetcher';

export type FuelType = 'petrol' | 'diesel' | 'electric' | 'hybrid' | 'lpg';

export type Vehicle = {
  id: number;
  vehicle_plate: string;
  vehicle_type: string;
  vin_number: string;
  engine_volume: string;
  total_weight: string;
  engine_power: string;
  fuel_type: FuelType;
  registration_date: string;
  insurer_name: string;
  policy_number: string;
  green_card_number: string;
  green_card_valid_until: string;
  vehicle_insurance: boolean;
  insurance_company_name: string;
  is_active: boolean;
};

export type VehiclePayload = Omit<Vehicle, 'id'>;

export type FetchVehiclesQuery = PaginationQuery & {
  search?: string;
};

export async function fetchVehicles({
  locale,
  authToken,
  query,
}: GenericRequest<{ authToken: string; query?: FetchVehiclesQuery }>) {
  return fetchData<{ data: Vehicle[]; meta: PaginationMeta }>({
    method: 'GET',
    uri: '/store/vehicles',
    locale,
    headers: buildAuthHeader(authToken),
    query,
  });
}

export async function createVehicle({
  locale,
  authToken,
  payload,
}: GenericRequest<{ authToken: string; payload: VehiclePayload }>) {
  return fetchData<{ data: Vehicle }>({
    method: 'POST',
    uri: '/store/vehicles',
    locale,
    headers: buildAuthHeader(authToken),
    body: payload,
  });
}

export async function updateVehicle({
  locale,
  authToken,
  id,
  payload,
}: GenericRequest<{ authToken: string; id: number; payload: VehiclePayload }>) {
  return fetchData<{ data: Vehicle }>({
    method: 'PUT',
    uri: `/store/vehicles/${id}`,
    locale,
    headers: buildAuthHeader(authToken),
    body: payload,
  });
}

export async function exportVehicles({
  locale,
  authToken,
  query,
}: GenericRequest<{ authToken: string; query?: Omit<FetchVehiclesQuery, 'page'> }>) {
  return fetchData<Blob>({
    method: 'GET',
    uri: '/store/vehicles/export',
    locale,
    headers: buildAuthHeader(authToken),
    query,
    responseType: 'blob',
  });
}

export async function deleteVehicle({
  locale,
  authToken,
  id,
}: GenericRequest<{ authToken: string; id: number }>) {
  return fetchData({
    method: 'DELETE',
    uri: `/store/vehicles/${id}`,
    locale,
    headers: buildAuthHeader(authToken),
  });
}
